import { createContext, useContext, type ReactNode } from 'react';
import { shownHonesty } from '@aithora/shared';
import { useBuildLabels } from '../ui/buildLabels';

/**
 * Demo building scope. When the server says the whole scope is one seeded demo building
 * (/auth/me demo_building, or View as demoBuilding on the viewed building), the figures
 * drop their per-figure SAMPLE tag; every other tag (MEASURED, MODELED, ESTIMATED) stays.
 * No banner (Andrew 2026-10-09: no "demo" on screen). A real building is never this
 * scope, so its SAMPLE figures keep their tag.
 */
const DemoScopeCtx = createContext<boolean>(false);

export function DemoScopeProvider({ demo, children }: { demo: boolean; children: ReactNode }) {
  return <DemoScopeCtx.Provider value={demo}>{children}</DemoScopeCtx.Provider>;
}

/**
 * A fleet or network view (one that can mix real and demo buildings) keeps every
 * per-figure tag, whatever the scope around it says.
 */
export function FleetScope({ children }: { children: ReactNode }) {
  return <DemoScopeCtx.Provider value={false}>{children}</DemoScopeCtx.Provider>;
}

/** True inside a single demo building scope (and not inside a FleetScope). */
export function useDemoBuilding(): boolean {
  return useContext(DemoScopeCtx);
}

/**
 * The tag a figure draws here: its label, or null for SAMPLE inside a demo building
 * scope (the banner already says so). For the pages that word their own badge.
 */
export function useShownHonesty(): <L extends string>(label: L | null) => L | null {
  const demo = useDemoBuilding();
  const build = useBuildLabels();
  // Build labels show to Developer and Super Admin only (ui/buildLabels).
  return (label) => (build ? shownHonesty(label, demo) : null);
}

/**
 * For a tag written into a line of text ("PAYER MIX · SAMPLE"): `joiner + word`, or
 * nothing for SAMPLE inside a demo building scope. Everywhere else the text is exactly
 * what it was.
 */
export function useTagSuffix(): (word: string | null | undefined, joiner?: string) => string {
  const demo = useDemoBuilding();
  const build = useBuildLabels();
  return (word, joiner = ' · ') => (!build || (demo && word === 'SAMPLE') ? '' : joiner + (word ?? ''));
}
